import React, { useState, useEffect, useCallback } from 'react';
import { subscribe, getErrors, clearErrors, ErrorType } from '@/utils/debugLogger';
import { Bug, X, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Debug Panel — плавающая панель для отображения ошибок
 *
 * Toggle: Ctrl+Shift+D или кнопка в углу
 * Автоматически появляется при новой ошибке
 */
const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [errors, setErrors] = useState(getErrors());
  const [newErrorFlash, setNewErrorFlash] = useState(false);

  // Подписка на новые ошибки
  useEffect(() => {
    const unsubscribe = subscribe((newErrors) => {
      setErrors(newErrors);
      // Flash при новой ошибке
      if (newErrors.length > errors.length) {
        setNewErrorFlash(true);
        setTimeout(() => setNewErrorFlash(false), 1000);
        // Автоматически открываем при ошибке
        if (!isOpen) {
          setIsOpen(true);
          setIsMinimized(false);
        }
      }
    });
    return unsubscribe;
  }, [errors.length, isOpen]);

  // Клавиатурный shortcut: Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setIsMinimized(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClear = useCallback(() => {
    clearErrors();
  }, []);

  const getTypeColor = (type) => {
    switch (type) {
      case ErrorType.API:
        return 'bg-orange-100 text-orange-800';
      case ErrorType.JS:
        return 'bg-red-100 text-red-800';
      case ErrorType.REACT:
        return 'bg-purple-100 text-purple-800';
      case ErrorType.PROMISE:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Кнопка toggle (всегда видна)
  const ToggleButton = () => (
    <button
      onClick={() => {
        setIsOpen(prev => !prev);
        setIsMinimized(false);
      }}
      className={`
        fixed bottom-20 right-4 z-[9999]
        w-12 h-12 rounded-full shadow-lg
        flex items-center justify-center
        transition-all duration-200
        ${errors.length > 0
          ? newErrorFlash
            ? 'bg-red-600 scale-110'
            : 'bg-red-500 hover:bg-red-600'
          : 'bg-gray-600 hover:bg-gray-700'
        }
      `}
      title="Debug Panel (Ctrl+Shift+D)"
    >
      <Bug className="w-6 h-6 text-white" />
      {errors.length > 0 && (
        <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border border-red-500">
          {errors.length > 99 ? '99+' : errors.length}
        </span>
      )}
    </button>
  );

  if (!isOpen) {
    return <ToggleButton />;
  }

  return (
    <>
      <ToggleButton />

      {/* Panel */}
      <div
        className={`
          fixed bottom-36 right-4 z-[9998]
          bg-white rounded-lg shadow-2xl border border-gray-200
          transition-all duration-200
          ${isMinimized ? 'w-64' : 'w-96'}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-t-lg border-b">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-gray-600" />
            <span className="font-semibold text-sm text-gray-700">
              Debug Panel
            </span>
            <span className="text-xs text-gray-500">
              ({errors.length})
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 rounded"
              title="Очистить"
            >
              <Trash2 className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => setIsMinimized(prev => !prev)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              {isMinimized ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="max-h-80 overflow-y-auto">
            {errors.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Ошибок нет
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {errors.map((entry) => (
                  <div
                    key={entry.id}
                    className="p-2 hover:bg-gray-50 text-xs"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-gray-400 font-mono">
                        {entry.timestamp}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeColor(entry.type)}`}
                      >
                        {entry.type}
                      </span>
                    </div>
                    <p className="text-gray-700 break-words">
                      {entry.message}
                    </p>
                    {entry.details && (
                      <details className="mt-1">
                        <summary className="text-gray-400 cursor-pointer hover:text-gray-600">
                          Детали
                        </summary>
                        <pre className="mt-1 p-1 bg-gray-50 rounded text-[10px] overflow-x-auto text-gray-600">
                          {JSON.stringify(entry.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer hint */}
        {!isMinimized && (
          <div className="px-3 py-1.5 bg-gray-50 rounded-b-lg border-t text-[10px] text-gray-400 text-center">
            Ctrl+Shift+D для toggle
          </div>
        )}
      </div>
    </>
  );
};

export default DebugPanel;
